import StorageService from '@/services/storage.service'
import storageKeys from '@/constants/storageKeys'
import { defaultFilters } from '@/models/Users/helpers'
import usersRepository from '@/repository/generatedRepository/usersRepository'
import TableOptions from '@/models/TableOptions'
import { omitEmptyDeepFilters, prepareFilters } from '@/helpers/filters'
import { saveImpersonationOrigin } from '@/services/token.service'

const state = {
  list: [],
  filters: StorageService.get(storageKeys.USERS_FILTERS) || { ...defaultFilters() },
  tableOptions: TableOptions.defaultTableOptions(),
  partnerGroupManagerList: [],
  partnerGroupManagerTableOptions: TableOptions.defaultTableOptions(),
}

const getters = {
  filters: state => state.filters,
  tableOptions: state => state.tableOptions,
  list: state => state.list,
  isOpenFilterPanel: state =>
    state.filters.clientOptions && state.filters.clientOptions.filterPanelExpanded,
  partnerGroupManagerList: state => state.partnerGroupManagerList,
  partnerGroupManagerTableOptions: state => state.partnerGroupManagerTableOptions,
}

const actions = {
  async loadData(context, payload) {
    const {signal, ...filters} = payload
    context.commit('setFilters', filters)
    StorageService.set(storageKeys.USERS_FILTERS, filters)
    const appliedFilters = omitEmptyDeepFilters(filters.filter)
    const appliedCfFilters = omitEmptyDeepFilters(filters.cfFilter)
    const preparedFilters = prepareFilters({
      ...filters,
      filter: appliedFilters,
      cfFilter: appliedCfFilters,
      signal
    })
    const { data } = await usersRepository.list(preparedFilters)
    context.commit('setList', { data, filters })
    return { products: data.items, pagination: data.pagination }
  },
  async loadPartnerGroupManagerData(context, filters) {
    const appliedFilters = omitEmptyDeepFilters(filters.filter)
    const appliedCfFilters = omitEmptyDeepFilters(filters.cfFilter)

    const preparedFilters = prepareFilters({
      ...filters,
      filter: {
        ...appliedFilters,
        role: 'MANAGER',
        enabled: 1
      },
      cfFilter: appliedCfFilters,
    })

    const { data } = await usersRepository.list(preparedFilters)

    context.commit('setPartnerGroupManagerList', { data, filters })

    return { products: data.items, pagination: data.pagination }
  },
  async searchUsers(context, filters) {
    const appliedFilters = omitEmptyDeepFilters(filters.filter)
    const preparedFilters = prepareFilters({
      ...filters,
      filter: appliedFilters,
    })

    const { data } = await usersRepository.list(preparedFilters)

    return { products: data.items, pagination: data.pagination }
  },
  async loadItem(context, id) {
    const { data } = await usersRepository.get(id)
    return data
  },
  createItem(context, payload) {
    return usersRepository.post(payload)
  },
  updateItem(context, payload) {
    const { id, ...data } = payload
    return usersRepository.put(id, data)
  },
  deleteItem(context, payload) {
    return usersRepository.delete(payload)
  },
  async loadSessions(context, id) {
    const { data } = await usersRepository.getSessions(id)
    return data
  },
  async deleteSession(context, sessionId) {
    return usersRepository.deleteSessions(sessionId)
  },
  async closeAllSessions(context, sessions ) {
    return Promise.all(sessions.map(s => usersRepository.deleteSessions(s.id)))
  },
  async reset2fa(context, id) {
    return usersRepository.put(id, { googleAuthenticatorSecret: '' })
  },
  async loginAs({ commit }, id) {
    const { data } = await usersRepository.loginAs(id)
    saveImpersonationOrigin()
    commit('AuthModule/setAuth', data, { root: true })
    commit('AuthModule/setImpersonating', true, { root: true })
    return data
  },
  async loadAuthLog(context, { userId }) {
    const filters = {
      filter: {
        createdBy: userId,
      },
    }
    const appliedFilters = omitEmptyDeepFilters(filters.filter)
    const preparedFilters = prepareFilters({
      ...filters,
      filter: appliedFilters,
    })

    const { data } = await usersRepository.getAuthLog(preparedFilters)
    return data
  },
  filtersStorageReset() {
    StorageService.remove(storageKeys.USERS_FILTERS)
  },
  resetSearchFilter({commit}){
    commit('clearSearch')
  },
  updateFiltersClientOptions({ commit, getters }, clientOptions) {
    const updatedFilters = {
      ...getters.filters,
      clientOptions,
    }
    commit('setFilters', updatedFilters)
    StorageService.set(storageKeys.USERS_FILTERS, updatedFilters)
  },
}

const mutations = {
  setList(state, { data, filters }) {
    state.list = [...data.items]
    state.tableOptions = new TableOptions(data.pagination, filters)
  },
  setPartnerGroupManagerList(state, { data, filters }) {
    state.partnerGroupManagerList = [...data.items]
    state.partnerGroupManagerTableOptions = new TableOptions(data.pagination, filters)
  },
  setFilters(state, filters) {
    state.filters = { ...filters }
  },
  clearSearch(state) {
    state.tableOptions.search = ''
  },
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
}
